<template>
    <div class="ma-4">
    <v-snackbar
      v-model="msg"
      top
      :color="snackbarColor"
      :timeout="3000"
      elevation="4"
      class="rounded-lg"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{
          snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
        }}</v-icon>
        {{ text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="msg = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-card class="rounded-lg">
      <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
        <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
          <v-icon>navigate_before</v-icon>
        </v-btn>

        <span class="text-h6 font-weight-medium grey--text text--darken-3">
          Monthly Fire Drill Report
        </span>

        <v-spacer></v-spacer>

        <report-list-actions
          :reportOptions="reportOptions"
          :downloadFilename="reportListFilename"
        ></report-list-actions>
      </v-app-bar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 grey lighten-5">
        <v-row dense>
          <v-col cols="12" md="2">
            <v-menu
              ref="calendarMenu"
              v-model="calendarMenu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="reportFilters.startDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                  v-bind="attrs"
                  v-on="on"
                  :rules='requiredField'
                  clearable
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilters.startDate"
                no-title
                scrollable
                color="primary"
                class="rounded-lg"
                :rules='requiredField'
                clearable
                :max=reportFilters.endDate
              >
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="calendarMenu = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.calendarMenu.save(date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-menu
              ref="calendar2Menu"
              v-model="calendar2Menu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="reportFilters.endDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                  v-bind="attrs"
                  v-on="on"
                  :rules='requiredField'
                  clearable
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilters.endDate"
                no-title
                scrollable
                color="primary"
                class="rounded-lg"
                :rules='requiredField'
              >
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="calendar2Menu = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.calendar2Menu.save(date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-select 
              label="Select a Program"
              :items="programs"
              item-text="Name"
              item-value="Name"
              v-model="reportFilters.selectedProgram"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
              clearable
            ></v-select>
          </v-col>
          
          <v-col cols="12" md="2">
            <v-select
              label="Client Status"
              :items="clientStatuses"
              v-model="reportFilters.clientStatus"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
              clearable
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select 
              label="Select a Client"
              :items="filteredClients"
              item-text="Name"
              item-value="._id"
              v-model="reportFilters.clientID"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
              clearable
            ></v-select>
          </v-col>

          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn
              color="primary"
              block
              height="40"
              class="rounded-lg"
              :loading="isLoading"
              elevation="1"
              @click="drillQuery()"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-card-text class="pa-6">
        <v-data-table
          :headers="headers"
          :items="drills"
          sort-by="Date"
          sort-asc
          class="elevation-1 rounded-lg"
          :loading="isLoading"
          loading-text="Loading Fire Drills"
          :server-items-length="itemCount"
          :page="page"
          :pageCount="pageCount"
          :options.sync="options"
          :footer-props="{
            'items-per-page-options': [5, 10, 20, 50],
            showFirstLastPage: true,
            'items-per-page-text': 'Rows per page:',
            class: 'rounded-lg',
          }"
        >
        </v-data-table>
      </v-card-text>
    </v-card>
    </div>
</template>
<script>
import axios from 'axios'
import ReportListActions  from '@/components/Report/ReportListActions.vue';
import { STATUS_ARRAY } from '../../enums/UserStatusEnum';
import ReportFilters from '@/mixins/reportFilters.js';

const url = require("../../client_config/config").url;

  export default {
    mixins: [
      ReportFilters,
    ],
    components: {
      ReportListActions
    },
    data: () => ({
    headers: [
      {
        text: 'Date',
        align: 'start',
        sortable: true,
        value: 'Date',
      },
      { text: "Client Name", value: "ClientName" },
      { text: "Program", value: "Programs" },
      { text: "Signature", value: "StaffSignature" },
    ],
    date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
    clients: [],
    drills: [],
    calendarMenu: false,
    calendar2Menu: false,
    report: '',
    clientName: '',
    isLoading: false,
    requiredField: [
        v => !!v || 'This is required',
    ],
    userState: localStorage.getItem('userState'),
    text: "",
    msg: "",
    programs: [],
    clientStatuses: STATUS_ARRAY,
    reportListFilename: 'Monthly Fire Drill Report',
    options: {},
    reportFilters:  {
        startDate: '',
        endDate: '',
        selectedProgram: '',
        clientStatus: '',
        clientID: '',
    },
    reportFilterName: 'ReportFilters_MonthlyFireDrill',
    snackbarColor: "success",
    itemCount: 0,
    pageCount: 0,
    page: 1,
  }),
  
  computed: {
      filteredClients() {
        let clients = this.clients;
        const selectedProgram = this.reportFilters.selectedProgram;
        const clientStatus = this.reportFilters.clientStatus;

        if(selectedProgram){
          clients = clients.filter(client => client.Program.includes(selectedProgram));
        }

        if (clientStatus != "" && clientStatus) {
          clients = clients.filter((client) => client.Status == clientStatus)
        }
        
        return clients; 
      },
      

      queryParams() {
        let {page, sortBy, sortDesc, itemsPerPage} = this.options;      
        const sortField = sortBy ? sortBy[0] : 'Date';
        const sortDirection = sortDesc ? sortDesc[0] : 'desc';
        const companyIds = localStorage.getItem('userCompanies');
  
        return {
          startDate: this.reportFilters.startDate,
          endDate: this.reportFilters.endDate,
          Company: JSON.parse(companyIds),
          clientID: this.reportFilters.clientID,
          State: this.userState,
          Programs: this.reportFilters.selectedProgram,
          clientStatus: this.reportFilters.clientStatus,
          sortField: sortField,
          sortDirection: sortDirection,
          page: page,
          limit: itemsPerPage,
        };
      },
  
      reportOptions() {
        return { 
            query: this.queryParams,
            endpoints: `fireDrill/print-all-pdf`,
            dynamic: true
        }
      }
  },

    created() {
      this.getEmployees();
      this.getClients();
      this.getPrograms();
      this.reportFilters = this.getReportFilters(this.reportFilterName) ?? this.reportFilters;
    },

    watch: {
      reportFilters: {
        handler(newVal) {
          this.setReportFilters(this.reportFilterName, newVal);
        },
        deep: true
      },
      options: {
        handler() {
          this.drillQuery();},
        deep: true
      }
    },

    methods: {
      initialize() {
        this.drills = [];
      },

      async getClients () {
        let params = {};
        let endpoint = `${url}/clients`;

        const companyIds = localStorage.getItem('userCompanies');

        params['Company:in'] = JSON.parse(companyIds);
        params['State'] = this.userState;

        await axios.get(endpoint,{params})
        .then((res) => {
            this.clients = res.data.clients;
        });

        // axios.get(`${url}/clients/state/${this.userState}`).then(async res => {
        //   this.clients = res.data.clients;
          
        // });      
      },

      async getEmployees () {
        let endpoint = '';

        endpoint = `${url}/employees/staff/${this.userState}`;
        const companyIds = localStorage.getItem('userCompanies');

        await axios.get(endpoint, {
          params: {
            Company: JSON.parse(companyIds)
          }
        }).then(res => {
          this.employees = res.data.employees;
          
        });    
      },

      close() {
        this.seditInfo = false;
        this.passdialog = false;
        this.chgimg = false;
      },
      
      async drillQuery() {
        this.drills = []
        this.isLoading = true

          axios.get(`${url}/fireDrill/`, {
            params: this.queryParams
          }).then(res => {
            this.drills = res.data.docs;
            this.pageCount = res.data.totalPages;
            this.itemCount = res.data.totalDocs;
          }).finally(() => {
            this.isLoading = false
          });

      },

      async getPrograms() {
      if(localStorage.getItem('userRole') == "Administration"){
        await axios.get(`${url}/units`).then(async res => {
        this.programs = []
        this.programs= res.data.units
      })
      } else {
        axios.get(`${url}/units/state/${this.userState}`).then(async res => {
          this.programs = []
          this.programs= res.data.units
        })
      }
    },

    printTable() {
      window.print()
      
    },
    }
  }
</script>
<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}

#print {
  display: none
}

@media only screen and (min-width: 768px) {
  .mobile {
    display: none;
  }
}

@media only screen and (max-width: 600px) {
  .desktop {
    display: none;
  }
}

@media print { 
  #print{ 
    display: block; 
    border: none; 
    width: 1200px;
    padding-right: 300px;
    margin-left: -200px;
  }
  
  nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
    display: none;
    border: none; 
  }

  @page { margin: 0; }
  body { margin: 1.6cm; }
} 
</style>